import React, { useEffect, useState, useRef, useCallback } from 'react';

/**
 * OtpResendButton (iter65)
 *
 * Reusable countdown-gated "Resend OTP" control used across:
 *   - Login.js (phone login)
 *   - CompleteProfile.js (Google → phone link)
 *   - PhoneVerifyGate.js (mandatory phone verify after Google signup)
 *   - EditProfile.js (email OTP — pass labelSingular="code")
 *
 * Behavior:
 *   - On mount, runs a 30-second countdown (configurable via `seconds`).
 *   - Button stays disabled while countdown > 0, shows "Resend OTP in {n}s".
 *   - At 0, becomes enabled with text "Resend OTP".
 *   - Clicking calls `onResend()`; on success, resets the countdown so the user can't
 *     spam the button.
 *   - Backend per-phone rate limit (also 30s) is a server-side safety net for the same.
 *
 * Props:
 *   onResend        async () => Promise — caller's send-otp action
 *   seconds         number (default 30) — countdown duration
 *   className       optional wrapper className
 *   resendLabel     string (default "Resend OTP")
 *   testIdPrefix    string (default "otp-resend") for stable data-testids
 */
const OtpResendButton = ({
  onResend,
  seconds = 30,
  className = '',
  resendLabel = 'Resend OTP',
  testIdPrefix = 'otp-resend',
}) => {
  const [remaining, setRemaining] = useState(seconds);
  const [sending, setSending] = useState(false);
  const timerRef = useRef(null);

  const startCountdown = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setRemaining(seconds);
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return r - 1;
      });
    }, 1000);
  }, [seconds]);

  // Kick off the first countdown on mount (assumes parent already triggered the
  // initial send-otp before mounting this button).
  useEffect(() => {
    startCountdown();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startCountdown]);

  const handleClick = async () => {
    if (remaining > 0 || sending) return;
    setSending(true);
    try {
      await onResend();
      startCountdown(); // restart only on success — on error caller toasts and user can retry
    } finally {
      setSending(false);
    }
  };

  const disabled = remaining > 0 || sending;
  const label = remaining > 0
    ? `${resendLabel} in ${remaining}s`
    : (sending ? 'Sending…' : resendLabel);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      data-testid={`${testIdPrefix}-btn`}
      aria-live="polite"
      className={`text-xs sm:text-sm font-semibold transition-colors ${disabled ? 'text-[#9CA3AF] cursor-not-allowed' : 'text-[#1800ad] hover:text-[#ff8500] cursor-pointer'} ${className}`}
    >
      <span data-testid={`${testIdPrefix}-label`}>{label}</span>
    </button>
  );
};

export default OtpResendButton;
